"use client";

import { IconChevronExpandYOutline18, IconDownload2Outline18 } from "nucleo-ui-outline-18";
import type { TurnkeyWallet } from "@/app/(v2)/_types/wallet";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useMemo, useRef, useState } from "react";
import { WalletOptionContent } from "./WalletListSelector";

interface SearchableWalletSelectProps {
  wallets: TurnkeyWallet[];
  selectedAddress?: string;
  onSelect: (address: string) => void;
  variant: "default" | "compact";
  onImportWallet?: () => void;
}

export function SearchableWalletSelect({
  wallets,
  selectedAddress,
  onSelect,
  variant,
  onImportWallet,
}: SearchableWalletSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedWallet = useMemo(
    () => wallets.find(wallet => wallet.address.toLowerCase() === selectedAddress?.toLowerCase()),
    [wallets, selectedAddress],
  );

  const filteredWallets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return wallets;

    return wallets.filter(wallet => {
      const name = wallet.walletName?.toLowerCase() || "";
      const address = wallet.address.toLowerCase();
      return name.includes(query) || address.includes(query);
    });
  }, [wallets, searchQuery]);

  const handleSelect = (address: string) => {
    onSelect(address);
    setOpen(false);
    setSearchQuery("");
  };

  return (
    <div ref={setContainerEl} className="w-full">
      <Popover
        modal
        open={open}
        onOpenChange={nextOpen => {
          setOpen(nextOpen);
          if (!nextOpen) setSearchQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border text-left transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
              variant === "compact" ? "px-3 py-2" : "px-3 py-2.5",
            )}
          >
            {selectedWallet ? (
              <span className="min-w-0 flex-1">
                <WalletOptionContent
                  wallet={selectedWallet}
                  index={wallets.indexOf(selectedWallet)}
                  isSelected={false}
                  variant="compact"
                  showInfoButton={false}
                  copyButtonSize="small"
                />
              </span>
            ) : (
              <span className="text-ds-text-disabled dark:text-ds-text-muted text-sm">
                Search and select a wallet...
              </span>
            )}
            <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="bg-ds-surface-card w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
          align="start"
          side="bottom"
          container={containerEl ?? undefined}
          onOpenAutoFocus={e => {
            e.preventDefault();
            searchInputRef.current?.focus({ preventScroll: true });
          }}
        >
          <Command shouldFilter={false}>
            <CommandInput
              ref={searchInputRef}
              placeholder={`Search ${wallets.length} wallets...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList className="max-h-64">
              <CommandEmpty>No wallets found.</CommandEmpty>
              <CommandGroup>
                {filteredWallets.map(wallet => {
                  const isSelected = wallet.address.toLowerCase() === selectedAddress?.toLowerCase();
                  return (
                    <CommandItem
                      key={wallet.address}
                      value={wallet.address}
                      onSelect={() => handleSelect(wallet.address)}
                      className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 cursor-pointer rounded-none px-3 py-2.5"
                    >
                      <div className="w-full">
                        <WalletOptionContent
                          wallet={wallet}
                          index={wallets.indexOf(wallet)}
                          isSelected={isSelected}
                          variant="compact"
                          showInfoButton={false}
                          copyButtonSize="small"
                        />
                      </div>
                    </CommandItem>
                  );
                })}
                {onImportWallet && (
                  <CommandItem
                    value="__import_wallet__"
                    onSelect={() => {
                      setOpen(false);
                      onImportWallet();
                    }}
                    className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 border-ds-border-default cursor-pointer rounded-none border-t px-3 py-2.5"
                  >
                    <div className="flex w-full items-center gap-2">
                      <IconDownload2Outline18 className="text-ds-text-subtle dark:text-ds-text-disabled h-4 w-4 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-ds-text-primary text-sm font-medium">Import Wallet</span>
                        <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">
                          Bring an existing private key into your organization
                        </p>
                      </div>
                    </div>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
